import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './baseQuery';
import { BackendResponse } from '../../types';

export interface ChatRoomMember {
  userId: number;
  fullName: string;
  email?: string;
}

export interface ChatRoom {
  id: string;
  requestNo?: string | null;
  title?: string | null;
  module?: string | null;
  members: ChatRoomMember[];
}

export interface ChatMessage {
  id: string;
  roomId: string;
  senderId: number;
  content?: string | null;
  filePath?: string | null;
  fileName?: string | null;
  mimeType?: string | null;
  isSystem?: boolean;
  createdAt: string;
  sender: {
    ID: number;
    FullName?: string | null;
    EmailID?: string | null;
  };
  readBy: (string | number)[]
}

type PagedMessages = {
  list: ChatMessage[];
  count: number;
  page: number;
  pageSize: number;
};

export const chatApi = createApi({
  reducerPath: 'chat',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Room', 'RoomMessages'],
  endpoints: builder => ({
    getRoomByRequestNo: builder.query<BackendResponse<{ room: ChatRoom; members: any[] }>, string>({
      query: requestNo => ({
        url: `/chat/rooms/by-request/${encodeURIComponent(requestNo)}`,
        method: 'GET',
      }),
      providesTags: (_r, _e, requestNo) => [{ type: 'Room', id: requestNo }],
    }),

    getRoomMessages: builder.query<BackendResponse<PagedMessages>, { roomId: string; page?: number; pageSize?: number }>({
      query: ({ roomId, page = 1, pageSize = 50 }) => ({
        url: `/chat/rooms/${roomId}/messages`,
        method: 'GET',
        params: { page, pageSize },
      }),
      providesTags: (_r, _e, { roomId }) => [{ type: 'RoomMessages', id: roomId }],
    }),

    sendMessage: builder.mutation<
      BackendResponse<ChatMessage>,
      { roomId: string; content?: string; filePath?: string; fileName?: string; mimeType?: string }
    >({
      query: ({ roomId, ...body }) => ({
        url: `/chat/rooms/${roomId}/messages`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { roomId }) => [{ type: 'RoomMessages', id: roomId }],
    }),

    createManualRoom: builder.mutation<BackendResponse<ChatRoom>, { title: string; memberIds: number[]; requestNo?: string; module?: string }>({
      query: body => ({
        url: '/chat/rooms/manual',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Room'],
    }),
    getChatAttachmentFile: builder.query<
      Blob,
      { attachmentId: string }
    >({
      query: ({ attachmentId }) => ({
        url: `/chat/file/${attachmentId}`,
        method: 'GET',
        responseHandler: response => response.blob(),
      }),
    }),
  }),
});

export const {
  useLazyGetChatAttachmentFileQuery,
  useGetRoomByRequestNoQuery,
  useLazyGetRoomByRequestNoQuery,
  useGetRoomMessagesQuery,
  useSendMessageMutation,
  useCreateManualRoomMutation,
} = chatApi;
